import { Alert, Button, DatePicker, Form, Input, Modal, notification, Radio, Select, Spin } from "antd";
import { useEffect, useState } from "react";
import moment from "moment";
import { adminDoctorPlanApi, adminSetPlanApi, billingPlansApi } from "../../../config/Api";
import { apiErrorMessage } from "../../../services/ApiError";

const LENGTHS = [
    { value: 1, label: '1 month' },
    { value: 3, label: '3 months' },
    { value: 6, label: '6 months' },
    { value: 12, label: '12 months' },
    { value: 'date', label: 'Until a date' },
    { value: 'never', label: 'Never ends' },
]

// admin: give a doctor any plan for any length without payment; it replaces their current plan now
export const SetPlanModal = ({ doctor, onClose, onSaved }) => {
    const [form] = Form.useForm()
    const [plans, setPlans] = useState([])
    const [current, setCurrent] = useState(null)
    const [saving, setSaving] = useState(false)
    const planId = Form.useWatch('plan_id', form)
    const length = Form.useWatch('length', form)

    useEffect(() => {
        if (!doctor) return
        form.resetFields()
        setCurrent(null)
        billingPlansApi().then((res) => setPlans(res.data.data)).catch((err) => notification.error({ message: apiErrorMessage(err) }))
        adminDoctorPlanApi(doctor.id).then((res) => setCurrent(res.data.data)).catch((err) => notification.error({ message: apiErrorMessage(err) }))
    }, [doctor])

    const plan = plans.find((p) => p.id === planId)
    const isFree = plan?.is_free

    const save = (values) => {
        const data = { user_id: doctor.id, plan_id: values.plan_id, note: values.note || null }
        if (!isFree) {
            if (values.length === 'never') data.never_ends = true
            else if (values.length === 'date') data.ends_at = values.ends_at.format('YYYY-MM-DD')
            else data.months = values.length
        }
        setSaving(true)
        adminSetPlanApi(data)
            .then((res) => {
                notification.success({ message: res.data.message })
                onSaved && onSaved()
                onClose()
            })
            .catch((err) => notification.error({ message: apiErrorMessage(err) }))
            .finally(() => setSaving(false))
    }

    return (
        <Modal open={!!doctor} title={doctor ? `Set plan for ${doctor.name}` : ''} onCancel={onClose} footer={null} destroyOnClose>
            {/* nothing to show once closed (the doctor is cleared while the dialog fades out) */}
            {!doctor ? null : !current ? <div className="text-center p-4"><Spin /></div> : (
                <Form form={form} layout="vertical" onFinish={save} initialValues={{ length: 12 }}>
                    <p className="text-muted mb-3">
                        Current plan: <b>{current.plan || 'None'}</b>
                        {current.plan && (current.ends_at ? ` until ${moment(current.ends_at).format('DD MMM YYYY')}` : ' · Never ends')}
                        {` · ${current.active_clients} active client${current.active_clients === 1 ? '' : 's'}`}
                    </p>

                    <Form.Item name="plan_id" label="Plan" rules={[{ required: true, message: 'Choose a plan.' }]}>
                        <Select placeholder="Choose a plan">
                            {plans.map((p) => (
                                <Select.Option key={p.id} value={p.id}>
                                    {p.name} ({p.active_clients.toLocaleString('en-IN')} active clients)
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {!isFree && (
                        <Form.Item name="length" label="How long">
                            <Radio.Group optionType="button" options={LENGTHS} />
                        </Form.Item>
                    )}
                    {!isFree && length === 'date' && (
                        <Form.Item name="ends_at" label="Ends on" rules={[{ required: true, message: 'Choose the end date.' }]}>
                            <DatePicker format="DD-MM-YYYY" className="w-100" disabledDate={(day) => day && day <= moment().endOf('day')} />
                        </Form.Item>
                    )}

                    <Form.Item name="note" label="Note (only admins see it)">
                        <Input.TextArea rows={2} maxLength={500} placeholder="e.g. In-house doctor" />
                    </Form.Item>

                    {current.paid_time_left && (
                        <Alert
                            type="warning"
                            showIcon
                            className="mb-3"
                            message={`${current.name} has a paid ${current.plan} plan until ${moment(current.ends_at).format('DD MMM YYYY')}. This replaces it now; the time left isn't refunded.`}
                        />
                    )}
                    {plan && current.active_clients > plan.active_clients && (
                        <Alert
                            type="warning"
                            showIcon
                            className="mb-3"
                            message={`${current.active_clients} clients are active but ${plan.name} allows ${plan.active_clients}. No new clients can be activated until some are turned off.`}
                        />
                    )}

                    <div className="text-end">
                        <Button onClick={onClose} className="me-2" disabled={saving}>Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={saving}>{saving ? 'Saving' : 'Set plan'}</Button>
                    </div>
                </Form>
            )}
        </Modal>
    )
}
